import { useRef, useState } from "react";
import { Controller } from "react-hook-form";
import AsyncSelect from "react-select/async";
import { useOptionsFetch } from "../../../../hooks/useOptionsFetch";
import { useTranslation } from "react-i18next";

/* GLOBAL SHARED CACHE */
const keywordCache = {
  initial: null, // initial paginated result
  search: {}, // search cache { query: [] }
  promise: null // prevents parallel initial calls
};

const KeywordAsyncSelect = ({
  name,
  control,
  fieldError,
  placeholder,
  excludeIds = [],
  isDisabled = false
}) => {
  const { t } = useTranslation();
  const { handleKeywordOptions } = useOptionsFetch();
  const debounceRef = useRef(null);
  const [loading, setLoading] = useState(false);

  /* FILTER OUT SELECTED IDS */
  const filterSelected = (options = []) =>
    options.filter((opt) => !excludeIds.includes(opt.id || opt._id));

  /* ASYNC LOADER */
  const loadOptions = (inputValue) => {
    if (isDisabled) return Promise.resolve([]);
    /* SEARCH MODE (debounced + cached) */
    if (inputValue) {
      if (keywordCache.search[inputValue]) {
        return Promise.resolve(filterSelected(keywordCache.search[inputValue]));
      }
      return new Promise((resolve) => {
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async () => {
          const data = await handleKeywordOptions({
            name: inputValue
          });
          keywordCache.search[inputValue] = data || [];
          resolve(filterSelected(data || []));
        }, 1000);
      });
    }
    /* INITIAL PAGINATED FETCH (CALL ONCE) */
    if (keywordCache.initial) {
      return Promise.resolve(filterSelected(keywordCache.initial));
    }
    if (!keywordCache.promise) {
      setLoading(true);
      keywordCache.promise = handleKeywordOptions()
        .then((data) => {
          keywordCache.initial = data || [];
          return keywordCache.initial;
        })
        .finally(() => {
          keywordCache.promise = null;
          setLoading(false);
        });
    }
    return keywordCache.promise.then(filterSelected);
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <AsyncSelect
          {...field}
          defaultOptions
          className="w-100 text-capitalize"
          isLoading={loading}
          cacheOptions={false} // manual cache
          isDisabled={isDisabled}
          loadOptions={loadOptions}
          placeholder={placeholder || t("common.select")}
          getOptionLabel={(opt) => opt.name}
          getOptionValue={(opt) => opt.id || opt._id}
          onChange={(option) => field.onChange(option)}
          styles={{
            control: (base) => ({
              ...base,
              borderColor: fieldError
                ? "var(--danger-color)"
                : "var(--field-border-color-regular)"
            })
          }}
        />
      )}
    />
  );
};

export default KeywordAsyncSelect;
